import React from 'react';
import {
  Box,
  Typography,
  Grid,
  TextField,
  FormControlLabel,
  Switch,
  Button,
  IconButton,
} from '@mui/material';
import {
  Instagram,
  Twitter,
  Facebook,
  Delete,
} from '@mui/icons-material';
import { MediaContent, SocialMediaPost } from '../../types/PostProduction';

interface MediaContentSectionProps {
  mediaContent: MediaContent;
  editMode: boolean;
  onChange: (field: string, value: any) => void;
  onNestedChange: (field: string, subfield: string, value: any, index?: number) => void;
  onSocialMediaAdd: (platform: 'instagram' | 'twitter' | 'facebook' | 'youtube' | 'other') => void;
  onRemoveSocialMedia: (index: number) => void;
}

const MediaContentSection: React.FC<MediaContentSectionProps> = ({
  mediaContent,
  editMode,
  onChange,
  onNestedChange,
  onSocialMediaAdd,
  onRemoveSocialMedia,
}) => {
  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Media Content
      </Typography>
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <FormControlLabel
            control={
              <Switch
                checked={mediaContent.photosUploaded}
                onChange={(e) => onChange('photosUploaded', e.target.checked)}
                disabled={!editMode}
              />
            }
            label="Photos Uploaded"
          />
          {mediaContent.photosUploaded && (
            <TextField
              label="Photo Location/Link"
              fullWidth
              value={mediaContent.photoLocation || ''}
              onChange={(e) => onChange('photoLocation', e.target.value)}
              disabled={!editMode}
              margin="normal"
            />
          )}
          <FormControlLabel
            control={
              <Switch
                checked={mediaContent.videoRecorded}
                onChange={(e) => onChange('videoRecorded', e.target.checked)}
                disabled={!editMode}
              />
            }
            label="Video Recorded"
          />
          <FormControlLabel
            control={
              <Switch
                checked={mediaContent.youtubeUploaded}
                onChange={(e) => onChange('youtubeUploaded', e.target.checked)}
                disabled={!editMode}
              />
            }
            label="YouTube Upload Complete"
          />
          {mediaContent.youtubeUploaded && (
            <TextField
              label="YouTube URL"
              fullWidth
              value={mediaContent.youtubeUrl}
              onChange={(e) => onChange('youtubeUrl', e.target.value)}
              disabled={!editMode}
              margin="normal"
            />
          )}
        </Grid>

        <Grid item xs={12} md={6}>
          <Typography variant="h6" gutterBottom>
            Social Media Posts
          </Typography>
          {mediaContent.socialMediaPosts.map((post, index) => (
            <Box
              key={index}
              sx={{ mb: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}
            >
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={3}>
                  <Box display="flex" alignItems="center">
                    {post.platform === 'instagram' && <Instagram color="primary" />}
                    {post.platform === 'twitter' && <Twitter color="primary" />}
                    {post.platform === 'facebook' && <Facebook color="primary" />}
                    <Typography variant="body2" sx={{ ml: 1 }}>
                      {post.platform}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={5}>
                  <TextField
                    label="URL"
                    fullWidth
                    size="small"
                    value={post.url}
                    onChange={(e) =>
                      onNestedChange(
                        'socialMediaPosts',
                        'url',
                        e.target.value,
                        index
                      )
                    }
                    disabled={!editMode}
                  />
                </Grid>
                <Grid item xs={2}>
                  <TextField
                    label="Engagement"
                    type="number"
                    fullWidth
                    size="small"
                    value={post.engagement}
                    onChange={(e) =>
                      onNestedChange(
                        'socialMediaPosts',
                        'engagement',
                        Number(e.target.value),
                        index
                      )
                    }
                    disabled={!editMode}
                  />
                </Grid>
                {editMode && (
                  <Grid item xs={2}>
                    <IconButton
                      color="error"
                      onClick={() => onRemoveSocialMedia(index)}
                      title="Remove social media post"
                    >
                      <Delete />
                    </IconButton>
                  </Grid>
                )}
              </Grid>
            </Box>
          ))}
          {editMode && (
            <Box sx={{ mt: 2 }}>
              <Button
                variant="outlined"
                startIcon={<Instagram />}
                onClick={() => onSocialMediaAdd('instagram')}
                sx={{ mr: 1 }}
              >
                Add Instagram
              </Button>
              <Button
                variant="outlined"
                startIcon={<Twitter />}
                onClick={() => onSocialMediaAdd('twitter')}
                sx={{ mr: 1 }}
              >
                Add Twitter
              </Button>
              <Button
                variant="outlined"
                startIcon={<Facebook />}
                onClick={() => onSocialMediaAdd('facebook')}
              >
                Add Facebook
              </Button>
            </Box>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default MediaContentSection;